import { useState } from "react";
import { CheckCircle, CreditCard, DollarSign, Calendar, Plus, ChevronDown, ChevronUp } from "lucide-react";

const Billing = () => {
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: "pm_1",
      type: "card",
      brand: "visa",
      last4: "4242",
      expMonth: 12,
      expYear: 2024,
      isDefault: true,
    },
    {
      id: "pm_2",
      type: "card",
      brand: "mastercard",
      last4: "5555",
      expMonth: 10,
      expYear: 2025,
      isDefault: false,
    }
  ]);

  // Sample billing history
  const billingHistory = [
    {
      id: "inv_1",
      date: "2023-11-01",
      amount: 49.99,
      status: "paid",
      description: "Pro Plan - Monthly",
      invoice_pdf: "#"
    },
    {
      id: "inv_2",
      date: "2023-10-01",
      amount: 49.99,
      status: "paid",
      description: "Pro Plan - Monthly",
      invoice_pdf: "#"
    },
    {
      id: "inv_3",
      date: "2023-09-01",
      amount: 49.99,
      status: "paid",
      description: "Pro Plan - Monthly",
      invoice_pdf: "#"
    }
  ];

  // Subscription plans
  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: 19.99,
      interval: "month",
      description: "Perfect for individuals and small businesses getting started.",
      features: [
        "Up to 3 websites",
        "Basic analytics",
        "Email support",
        "1 user account"
      ],
      isPopular: false
    },
    {
      id: "pro",
      name: "Professional",
      price: 49.99,
      interval: "month",
      description: "Great for growing businesses that need more features.",
      features: [
        "Up to 10 websites",
        "Advanced analytics",
        "Priority email & chat support",
        "5 user accounts",
        "Custom reporting"
      ],
      isPopular: true
    },
    {
      id: "business",
      name: "Business",
      price: 99.99,
      interval: "month",
      description: "For larger organizations with advanced requirements.",
      features: [
        "Unlimited websites",
        "Enterprise-grade analytics",
        "24/7 phone, email & chat support",
        "Unlimited user accounts",
        "Custom integrations",
        "Dedicated account manager"
      ],
      isPopular: false
    }
  ];

  // Set a payment method as default
  const setDefaultPaymentMethod = (id) => {
    setPaymentMethods(paymentMethods.map(method => ({
      ...method,
      isDefault: method.id === id
    })));
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // Handle plan selection
  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  // Format credit card brand for display
  const formatCardBrand = (brand) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  // Format card expiration date
  const formatExpiry = (month, year) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  return (
    <div className="container p-4 mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        <p className="text-gray-500">Manage your subscription plan and payment methods</p>
      </div>

      {/* Current Plan */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold">Current Plan</h2>
            <p className="text-gray-500">You are currently on the {plans.find(plan => plan.id === selectedPlan)?.name} plan.</p>
          </div>
          <div className="mt-4 md:mt-0">
            <div className="flex items-center text-2xl font-bold">
              ${plans.find(plan => plan.id === selectedPlan)?.price}
              <span className="text-sm font-normal text-gray-500 ml-1">/{plans.find(plan => plan.id === selectedPlan)?.interval}</span>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-medium mb-2">Included in your plan:</h3>
          <ul className="space-y-2">
            {plans.find(plan => plan.id === selectedPlan)?.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Available Plans */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-6">Available Plans</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`border rounded-lg p-6 ${
                plan.isPopular ? "border-blue-500 relative" : "border-gray-200"
              } ${selectedPlan === plan.id ? "ring-2 ring-blue-500" : ""}`}
            >
              {plan.isPopular && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                  POPULAR
                </div>
              )}
              
              <h3 className="text-lg font-semibold">{plan.name}</h3>
              <div className="mt-2 flex items-baseline">
                <span className="text-2xl font-bold">${plan.price}</span>
                <span className="ml-1 text-gray-500">/{plan.interval}</span>
              </div>
              
              <p className="mt-4 text-gray-600 text-sm">{plan.description}</p>
              
              <ul className="mt-6 space-y-2">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <button
                className={`mt-6 w-full py-2 px-4 rounded-md ${
                  selectedPlan === plan.id
                    ? "bg-green-100 text-green-700 border border-green-300"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
                onClick={() => handlePlanSelect(plan.id)}
                disabled={selectedPlan === plan.id}
              >
                {selectedPlan === plan.id ? "Current Plan" : "Select Plan"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-6">Payment Methods</h2>
        
        <div className="space-y-4">
          {paymentMethods.map((method) => (
            <div 
              key={method.id}
              className={`border rounded-lg p-4 ${
                method.isDefault ? "border-green-500 bg-green-50" : "border-gray-200"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center">
                  <CreditCard className="h-6 w-6 text-gray-500 mr-3" />
                  <div>
                    <div className="font-medium">
                      {formatCardBrand(method.brand)} •••• {method.last4}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      Expires {formatExpiry(method.expMonth, method.expYear)}
                      {method.isDefault && (
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex">
                  {!method.isDefault && (
                    <button
                      className="text-sm text-blue-600 hover:text-blue-800 mr-4"
                      onClick={() => setDefaultPaymentMethod(method.id)}
                    >
                      Set as default
                    </button>
                  )}
                  <button className="text-sm text-red-600 hover:text-red-800">
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          <button className="mt-4 flex items-center text-blue-600 hover:text-blue-800">
            <Plus className="h-4 w-4 mr-1" />
            Add Payment Method
          </button>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <div 
          className="flex items-center justify-between cursor-pointer" 
          onClick={() => setShowBillingHistory(!showBillingHistory)}
        >
          <h2 className="text-xl font-semibold">Billing History</h2>
          <button className="text-gray-500">
            {showBillingHistory ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {showBillingHistory && (
          <div className="mt-6">
            {billingHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Download</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billingHistory.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(invoice.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
                            {invoice.amount.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <a href={invoice.invoice_pdf} className="text-blue-600 hover:text-blue-900">
                            Download
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No billing history available yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;